// ✅ src/pages/TarMixing/ProductionProcess.jsx
// Updated with safe localStorage handling + common headers

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const toNum = (v) => {
  const n = Number(
    String(v ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const fmt2 = (v) => toNum(v).toFixed(2);
const fmt3 = (v) => toNum(v).toFixed(3);

const isoDate = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function ProductionProcess() {
  const { productionId: paramId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const stateId = location.state?.id;
  const editMode = location.state?.editMode || false;

  const productionId = stateId || paramId;
  const isEdit = editMode && !!productionId;

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchtype = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS],
  );
  const userid = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid],
  );

  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // dropdown sources
  const [projects, setProjects] = useState([]); // [{id,name}]
  const [productions, setProductions] = useState([]); // [{id,name}]

  // header form
  const [formData, setFormData] = useState({
    date: isoDate(new Date()),
    projectCode: "",
    projectName: "",
    bomId: 0,
    productionName: "",
    productionCode: "",
    workTypeName: "",
    productionUOM: "",
    productionQuantity: "0",
  });

  const [items, setItems] = useState([]);

  const [otherItems, setOtherItems] = useState(
    Array.from({ length: 5 }).map(() => ({ name: "", qty: 0, price: 0 })),
  );

  const [extraInputs, setExtraInputs] = useState({
    l1: "",
    l2: "",
    l3: "",
    l4: "",
    l5: "",
    l6: "",
    l7: "",
    l8: "",
    l9: "",
    l10: "",
    r1: "",
    r2: "",
    r3: "",
    r4: "",
  });

  const totalAmount = useMemo(() => {
    const gridTotal = items.reduce(
      (sum, r) => sum + toNum(r.materialQty) * toNum(r.price),
      0,
    );
    const otherTotal = otherItems.reduce(
      (sum, r) => sum + toNum(r.qty) * toNum(r.price),
      0,
    );
    return gridTotal + otherTotal;
  }, [items, otherItems]);

  const handleBack = () => navigate("/TarMixing/Tar_Production_Listing");

  // =========================================================
  // 1) LOAD PROJECTS
  // =========================================================
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${BASE_URL}/api/productionEntry/projects`,
          {
            params: { branchid, userid, branchtype },
            headers: commonHeaders,
          },
        );
        setProjects(res.data?.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load projects");
      } finally {
        setLoading(false);
      }
    })();
  }, [branchid, userid, branchtype, commonHeaders]);

  // =========================================================
  // 2) LOAD PRODUCTIONS BY PROJECT
  // =========================================================
  const loadProductions = async (projectCode) => {
    if (!projectCode) {
      setProductions([]);
      return;
    }

    try {
      setLoadingDetails(true);
      const prodRes = await axios.get(
        `${BASE_URL}/api/productionEntry/productions`,
        {
          params: { branchid, userid, projectCode },
          headers: commonHeaders,
        },
      );
      setProductions(prodRes.data?.data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load production list",
      );
      setProductions([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const onProjectChange = async (e) => {
    const projectCode = String(e.target.value || "");
    const p = projects.find((x) => String(x.id) === String(projectCode));

    setFormData((prev) => ({
      ...prev,
      projectCode,
      projectName: p?.name || "",
      bomId: 0,
      productionName: "",
      productionCode: "",
      workTypeName: "",
      productionUOM: "",
      productionQuantity: "0",
    }));

    setItems([]);
    setOtherItems(
      Array.from({ length: 5 }).map(() => ({ name: "", qty: 0, price: 0 })),
    );

    await loadProductions(projectCode);
  };

  // =========================================================
  // 3) LOAD BOM HEADER + ITEMS
  // =========================================================
  const loadBomItems = async ({ projectCode, bomId }) => {
    if (!projectCode || !bomId) return;

    try {
      setLoadingDetails(true);
      const res = await axios.get(`${BASE_URL}/api/productionEntry/bom-items`, {
        params: { branchid, userid, projectCode, bomId },
        headers: commonHeaders,
      });

      const data = res.data?.data || {};
      const header = data.header || {};
      const rows = data.items || [];

      setFormData((prev) => ({
        ...prev,
        bomId: Number(header.bomId || bomId),
        productionName: String(header.productionName || prev.productionName),
        productionCode: String(header.productionCode || ""),
        workTypeName: String(header.workTypeName || ""),
        productionUOM: String(header.uomName || ""),
      }));

      setItems(
        rows.map((r, idx) => ({
          sno: idx + 1,
          itemName: r.itemName || "",
          uomName: r.uomName || header.uomName || "",
          bomQty: toNum(r.bomQty),
          materialQty: toNum(r.materialQty ?? 0),
          price: toNum(r.price ?? 0),
          itemId: r.itemId ?? null,
          itemCode: r.itemCode ?? null,
        })),
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load BOM items");
      setItems([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const onProductionChange = async (e) => {
    const bomId = Number(e.target.value || 0);
    const prod = productions.find((x) => Number(x.id) === bomId);

    setFormData((prev) => ({
      ...prev,
      bomId,
      productionName: prod?.name || "",
      productionCode: "",
      workTypeName: "",
      productionUOM: "",
    }));

    setItems([]);

    if (formData.projectCode && bomId) {
      await loadBomItems({ projectCode: formData.projectCode, bomId });
    }
  };

  // =========================================================
  // 4) EDIT MODE LOAD
  // =========================================================
  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${BASE_URL}/api/productionListing/getById`,
          {
            params: { branchid, id: productionId },
            headers: commonHeaders,
          },
        );

        const data = res.data?.data;
        if (!data) return toast.error("Record not found");

        const master = data.master || {};
        const details = data.details || [];
        const others = data.others || [];

        const prodQty = toNum(master.ProdQuantity);

        setFormData((p) => ({
          ...p,
          date: isoDate(master.fld_ProductionDate),
          projectCode: String(master.ProjectCode || ""),
          projectName: "",
          bomId: Number(master.fld_bomid || 0),
          productionName: String(master.ProductionName || ""),
          productionCode: String(master.ProductionCode || ""),
          workTypeName: String(master.TarItemCode || ""),
          productionUOM: String(master.fld_UOMName || ""),
          productionQuantity: String(master.ProdQuantity ?? "0"),
        }));

        if (master.ProjectCode) {
          await loadProductions(String(master.ProjectCode));
        }

        setItems(
          details.map((r, idx) => {
            const materialQty = toNum(r.Quantity);
            const price = toNum(r.fld_Price);
            const bomQty = prodQty > 0 ? materialQty / prodQty : 0;

            return {
              sno: idx + 1,
              itemName: r.ItemName || "",
              uomName: String(master.fld_UOMName || ""),
              bomQty,
              materialQty,
              price,
              itemId: r.fld_itemID ?? null,
              itemCode: r.ItemCode ?? null,
            };
          }),
        );

        setOtherItems(
          Array.from({ length: 5 }).map((_, i) => {
            const row = others[i] || {};
            return {
              name: row.ItemName || row.name || "",
              qty: toNum(row.Quantity || row.qty || 0),
              price: toNum(row.fld_Price || row.price || 0),
            };
          }),
        );
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load record");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, productionId, branchid, commonHeaders]);

  // Fill project name after projects load
  useEffect(() => {
    if (!isEdit) return;
    if (!formData.projectCode) return;
    if (formData.projectName) return;

    const p = projects.find(
      (x) => String(x.id) === String(formData.projectCode),
    );
    if (p?.name) {
      setFormData((prev) => ({ ...prev, projectName: p.name }));
    }
  }, [projects, isEdit, formData.projectCode, formData.projectName]);

  // =========================================================
  // 5) SAVE / UPDATE
  // =========================================================
  const handleSave = async () => {
    if (!formData.date) return toast.error("Date required");
    if (!formData.projectCode) return toast.error("Project required");
    if (!formData.bomId) return toast.error("Production required");

    const pq = toNum(formData.productionQuantity);
    if (!Number.isFinite(pq) || pq <= 0)
      return toast.error("Production Quantity must be > 0");
    if (!items.length) return toast.error("No items loaded");

    const payload = {
      branchid,
      userid,
      branchtype,
      date: formData.date,
      projectCode: String(formData.projectCode),
      bomId: Number(formData.bomId),
      productionName: formData.productionName,
      productionCode: formData.productionCode,
      workTypeName: formData.workTypeName,
      productionQuantity: pq,
      productionUOM: formData.productionUOM,
      items: items.map((r) => ({
        itemName: r.itemName,
        itemCode: r.itemCode ?? null,
        itemId: r.itemId ?? null,
        bomQty: toNum(r.bomQty),
        price: toNum(r.price),
      })),
      otherItems: otherItems
        .filter(
          (x) =>
            String(x.name || "").trim() !== "" ||
            toNum(x.qty) !== 0 ||
            toNum(x.price) !== 0,
        )
        .map((x) => ({
          name: String(x.name || ""),
          qty: toNum(x.qty),
          price: toNum(x.price),
        })),
      extraInputs,
    };

    try {
      setLoading(true);

      if (isEdit) {
        const res = await axios.put(
          `${BASE_URL}/api/productionEntry/update/${productionId}`,
          payload,
          { headers: commonHeaders },
        );
        if (res.data?.success === false) {
          return toast.error(res.data?.message || "Update failed");
        }
        toast.success(res.data?.message || "Updated!");
      } else {
        const res = await axios.post(
          `${BASE_URL}/api/productionEntry/submit`,
          payload,
          { headers: commonHeaders },
        );
        if (res.data?.success === false) {
          return toast.error(res.data?.message || "Save failed");
        }
        toast.success(res.data?.message || "Saved!");
      }

      navigate("/TarMixing/ProductionListing");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">PRODUCTION PROCESS</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div className="flex justify-start mt-2">
          <button
            onClick={handleBack}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
          >
            <FaArrowLeft /> Back
          </button>
        </div>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-6">
            <div className="space-y-4">
              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  disabled={isEdit}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, date: e.target.value }))
                  }
                  className={`w-full px-3 py-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Production Name <span className="text-red-500">*</span>
                </label>

                {isEdit ? (
                  <input
                    type="text"
                    value={formData.productionName}
                    readOnly
                    className={`w-full px-3 py-2 border rounded ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-gray-100 border-gray-300 text-gray-900"
                    }`}
                  />
                ) : (
                  <select
                    value={formData.bomId || 0}
                    onChange={onProductionChange}
                    disabled={!formData.projectCode}
                    className={`w-full px-3 py-2 border rounded ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value={0}>-- Select --</option>
                    {productions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Work Type Name
                </label>
                <input
                  type="text"
                  value={formData.workTypeName}
                  readOnly
                  className={`w-full px-3 py-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-gray-100 border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Production UOM
                </label>
                <input
                  type="text"
                  value={formData.productionUOM}
                  readOnly
                  className={`w-full px-3 py-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-gray-100 border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Project Name <span className="text-red-500">*</span>
                </label>

                {isEdit ? (
                  <input
                    type="text"
                    value={formData.projectName || formData.projectCode}
                    readOnly
                    className={`w-full px-3 py-2 border rounded ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-gray-100 border-gray-300 text-gray-900"
                    }`}
                  />
                ) : (
                  <select
                    value={formData.projectCode}
                    onChange={onProjectChange}
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">-- Select --</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Production Code
                </label>
                <input
                  type="text"
                  value={formData.productionCode}
                  readOnly
                  className={`w-full px-3 py-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-gray-100 border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div className="grid grid-cols-[170px_1fr] items-center gap-4">
                <label
                  className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}
                >
                  Production Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.productionQuantity}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      productionQuantity: e.target.value,
                    }))
                  }
                  className={`w-full px-3 py-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}
          >
            <div
              className={`${isDark ? "bg-gray-800" : "bg-blue-100"} px-4 py-3 font-semibold`}
            >
              {loadingDetails ? "Loading..." : "Items"}
            </div>

            {items.length === 0 ? (
              <div
                className={`p-10 text-center ${isDark ? "bg-gray-900 text-gray-400" : "bg-white text-gray-500"}`}
              >
                No items found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className={`min-w-full text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}
                >
                  <thead
                    className={`${isDark ? "bg-gray-800" : "bg-gray-100"}`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold">
                        S.no
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Item Name
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        UOM
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        BOM Quantity
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Material Quantity
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Price
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "bg-gray-900" : "bg-white"}`}>
                    {items.map((r, idx) => {
                      const amount = toNum(r.materialQty) * toNum(r.price);
                      return (
                        <tr
                          key={idx}
                          className={`border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}
                        >
                          <td className="px-4 py-3 text-center">{r.sno}</td>
                          <td className="px-4 py-3">{r.itemName}</td>
                          <td className="px-4 py-3 text-center">{r.uomName}</td>
                          <td className="px-4 py-3 text-right">
                            {fmt3(r.bomQty)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {fmt3(r.materialQty)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {fmt2(r.price)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {fmt2(amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <div className="pt-4">
                <div className="font-semibold mb-2">Other Materials</div>
                <div className="space-y-2">
                  {otherItems.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2">
                      <input
                        className={`col-span-6 px-3 py-2 border rounded ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Item name"
                        value={row.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setOtherItems((p) =>
                            p.map((r, i) =>
                              i === idx ? { ...r, name: v } : r,
                            ),
                          );
                        }}
                      />
                      <input
                        type="number"
                        className={`col-span-3 px-3 py-2 border rounded text-right ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Qty"
                        value={row.qty}
                        onChange={(e) => {
                          const v = e.target.value;
                          setOtherItems((p) =>
                            p.map((r, i) => (i === idx ? { ...r, qty: v } : r)),
                          );
                        }}
                      />
                      <input
                        type="number"
                        className={`col-span-3 px-3 py-2 border rounded text-right ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Price"
                        value={row.price}
                        onChange={(e) => {
                          const v = e.target.value;
                          setOtherItems((p) =>
                            p.map((r, i) =>
                              i === idx ? { ...r, price: v } : r,
                            ),
                          );
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-4 font-semibold">
                  Total : {fmt2(totalAmount)}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-10">
            <button
              onClick={handleSave}
              disabled={loading}
              className={`ml-auto px-10 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              } bg-green-600 hover:bg-green-700 text-white`}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
}
